import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, ChevronLeft, ChevronRight, Loader2, Captions } from "lucide-react";
import api from "../../lib/api";
import { AnimatedDiagram } from "./AnimatedDiagram";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SPEEDS = [0.75, 1, 1.25, 1.5];
const sentences = (t) => (t || "").match(/[^.!?।]+[.!?।]*/g)?.map((s) => s.trim()).filter(Boolean) || [t];

export function LessonPlayer({ lesson, accent, onFinished }) {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [subs, setSubs] = useState(true);
  const [line, setLine] = useState(0);
  const [replayKey, setReplayKey] = useState(0);
  const audio = useRef(null);
  const scene = lesson.scenes[idx];
  const lines = sentences(scene.narration);

  const stop = () => { if (audio.current) { audio.current.pause(); audio.current = null; } setPlaying(false); };

  const play = async () => {
    stop(); setLoading(true); setLine(0);
    try {
      const { data } = await api.get(`/learn/lessons/${lesson.id}/scenes/${idx}/audio`);
      const a = new Audio(`${BACKEND}${data.url}`);
      a.playbackRate = speed;
      const total = lines.reduce((s, l) => s + l.length, 0) || 1;
      a.ontimeupdate = () => {
        const frac = a.duration ? a.currentTime / a.duration : 0;
        let acc = 0, i = 0;
        for (; i < lines.length; i++) { acc += lines[i].length / total; if (frac <= acc) break; }
        setLine(Math.min(i, lines.length - 1));
      };
      a.onended = () => {
        setPlaying(false);
        if (idx < lesson.scenes.length - 1) setTimeout(() => setIdx((v) => v + 1), 700);
        else onFinished?.();
      };
      audio.current = a;
      await a.play(); setPlaying(true);
    } catch { setPlaying(false); }
    finally { setLoading(false); }
  };

  useEffect(() => { setReplayKey((k) => k + 1); if (idx > 0) play(); return stop; /* eslint-disable-next-line */ }, [idx]);
  useEffect(() => { if (audio.current) audio.current.playbackRate = speed; }, [speed]);

  const toggle = () => {
    if (playing) { audio.current?.pause(); setPlaying(false); }
    else if (audio.current) { audio.current.play(); setPlaying(true); }
    else play();
  };
  const replay = () => { setReplayKey((k) => k + 1); play(); };

  return (
    <div className="space-y-4" data-testid="lesson-player">
      <div className="flex items-center justify-between">
        <div className="label-mono">Scene {idx + 1} / {lesson.scenes.length}</div>
        <div className="flex gap-1">{lesson.scenes.map((_, i) => (
          <span key={i} className="h-1 w-6 rounded-full transition-colors" style={{ background: i <= idx ? accent : "rgba(255,255,255,0.1)" }} />
        ))}</div>
      </div>
      <h3 className="font-heading text-lg font-normal tracking-wide" data-testid="scene-heading">{scene.heading}</h3>
      <div className="relative">
        <AnimatedDiagram diagram={scene.diagram} accent={accent} sceneKey={`${idx}-${replayKey}`} />
        {subs && (
          <div data-testid="lesson-subtitles" className="mt-2 text-center rounded-xl bg-black/60 border border-white/5 px-4 py-2 text-[14px] reading min-h-[2.75rem]">
            {lines[line]}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="scene-prev" disabled={idx === 0} onClick={() => setIdx(idx - 1)} className="w-9 h-9 rounded-full glass-card flex items-center justify-center disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
        <button data-testid="lesson-play" onClick={toggle} className="w-11 h-11 rounded-full flex items-center justify-center text-[#000000]" style={{ background: `linear-gradient(90deg,#FFFFFF,${accent})` }}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button data-testid="scene-next" disabled={idx === lesson.scenes.length - 1} onClick={() => setIdx(idx + 1)} className="w-9 h-9 rounded-full glass-card flex items-center justify-center disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
        <button data-testid="lesson-replay" onClick={replay} className="w-9 h-9 rounded-full glass-card flex items-center justify-center" title="Replay scene"><RotateCcw className="w-4 h-4" /></button>
        <button data-testid="lesson-subs-toggle" onClick={() => setSubs(!subs)} className={`w-9 h-9 rounded-full glass-card flex items-center justify-center ${subs ? "text-mono-300" : "text-slate-500"}`} title="Subtitles"><Captions className="w-4 h-4" /></button>
        <div className="ml-auto flex gap-1" data-testid="lesson-speed">
          {SPEEDS.map((s) => (
            <button key={s} data-testid={`speed-${s}`} onClick={() => setSpeed(s)}
              className={`rounded-full px-2.5 py-1 text-xs ${speed === s ? "bg-mono-400/20 text-mono-200" : "text-slate-400 hover:text-white"}`}>{s}x</button>
          ))}
        </div>
      </div>
      <p className="reading text-sm text-slate-400" data-testid="scene-narration">{scene.narration}</p>
    </div>
  );
}
